import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { BRAND } from '../../config/brand';
import { CommandPalette } from './CommandPalette';
import { useWorldTheme } from '../../hooks/useWorldTheme';

/**
 * Platform-admin app shell (event-platform operator space).
 *
 * A standalone, indigo-branded layout with its own left nav — deliberately
 * separate from the KDU-inherited tenant hub (AppLayout). Houses the global
 * cross-tenant surfaces: Overview, Tenants, Capabilities.
 */

interface NavLinkDef {
  to: string;
  label: string;
  icon: string;
  end?: boolean;
  adminOnly?: boolean;
}

const NAV: NavLinkDef[] = [
  { to: '/platform', label: 'Overview', icon: '📊', end: true },
  { to: '/platform/admin', label: 'System Admin', icon: '🛰️', adminOnly: true },
  { to: '/platform/organizations', label: 'Organizations', icon: '🏢' },
  { to: '/platform/events', label: 'Events', icon: '📅' },
  { to: '/platform/fundraising', label: 'Giving', icon: '💚' },
  { to: '/platform/venues', label: 'Venues', icon: '📍' },
  { to: '/platform/operators', label: 'Operators', icon: '🛡️', adminOnly: true },
  { to: '/platform/workflows', label: 'Workflows', icon: '📋' },
  { to: '/platform/offerings', label: 'Offerings', icon: '🏷️' },
  { to: '/platform/demos', label: 'System & Process Demos', icon: '🎬' },
  { to: '/platform/capabilities', label: 'Capabilities', icon: '🧩' },
];

export function PlatformLayout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'PlatformAdmin' || user?.role === 'Admin';
  const [paletteOpen, setPaletteOpen] = useState(false);
  useWorldTheme('operator'); // tint the operator world (indigo)

  // Global ⌘K / Ctrl-K to open the command palette.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function handleLogout() {
    logout();
    navigate('/login', { replace: true });
  }

  return (
    <div style={styles.app}>
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onCreateOrg={() => navigate('/platform/organizations?new=1')}
      />
      {/* Sidebar */}
      <aside style={styles.sidebar}>
        <a
          href={BRAND.marketingUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ ...styles.brand, textDecoration: 'none' }}
          title={`Go to ${BRAND.marketingUrl.replace('https://', '')}`}
          aria-label={`Open ${BRAND.name} marketing site in a new tab`}
        >
          <img
            src={BRAND.markWhite}
            alt=""
            aria-hidden="true"
            style={{ height: 28, width: 'auto', objectFit: 'contain', flexShrink: 0, display: 'block' }}
          />
          <div>
            <div style={styles.brandName}>{BRAND.name}</div>
            <div style={styles.brandSub}>Operator</div>
          </div>
        </a>

        <button style={styles.cmdkTrigger} onClick={() => setPaletteOpen(true)} aria-label="Open command palette">
          <span style={styles.cmdkIcon}>🔍</span>
          <span style={styles.cmdkText}>Search or jump to…</span>
          <span style={styles.cmdkKbd}>⌘K</span>
        </button>

        <nav style={styles.nav} aria-label="Platform admin">
          {NAV.filter((item) => !item.adminOnly || isAdmin).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              style={({ isActive }) => ({
                ...styles.navLink,
                ...(isActive ? styles.navLinkActive : null),
              })}
            >
              <span style={styles.navIcon}>{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div style={styles.sidebarFoot}>
          <div style={styles.who}>
            <div style={styles.whoName}>{user?.fullName || 'Operator'}</div>
            <div style={styles.whoEmail}>{user?.email}</div>
          </div>
          <button style={styles.logoutBtn} onClick={handleLogout}>
            Sign out
          </button>
        </div>
      </aside>

      {/* Content */}
      <main style={styles.content}>{children}</main>
    </div>
  );
}

const INK = '#0b1120';
const INK_SOFT = '#131b2e';
const ACCENT = '#4f46e5';

const styles: Record<string, React.CSSProperties> = {
  app: {
    display: 'flex',
    minHeight: '100vh',
    background: '#f6f7fb',
    color: INK,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  sidebar: {
    width: 240,
    flexShrink: 0,
    background: INK,
    color: '#e2e8f0',
    display: 'flex',
    flexDirection: 'column',
    position: 'sticky',
    top: 0,
    height: '100vh',
  },
  brand: { display: 'flex', alignItems: 'center', gap: 12, padding: '20px 20px 18px' },
  logoDot: { width: 16, height: 16, borderRadius: 5, background: 'var(--ep-accent, #4f46e5)', display: 'inline-block' },
  brandName: { fontWeight: 800, fontSize: '1rem', letterSpacing: '0.01em', color: '#fff' },
  brandSub: { fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '2px', color: '#9aa4b2' },
  cmdkTrigger: {
    display: 'flex', alignItems: 'center', gap: 8, margin: '0 12px 6px', padding: '9px 11px',
    background: '#131b2e', border: '1px solid #26324a', borderRadius: 9, color: '#9aa4b2',
    cursor: 'pointer', fontSize: '0.82rem', width: 'calc(100% - 24px)', textAlign: 'left',
  },
  cmdkIcon: { fontSize: '0.85rem' },
  cmdkText: { flex: 1, color: '#cbd5e1' },
  cmdkKbd: { fontSize: '0.68rem', background: '#0b1120', border: '1px solid #334155', borderRadius: 5, padding: '2px 6px', color: '#e2e8f0', fontWeight: 700 },
  nav: { display: 'flex', flexDirection: 'column', gap: 2, padding: '8px 12px', flex: 1 },
  navLink: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    borderRadius: 8,
    color: '#cbd5e1',
    textDecoration: 'none',
    fontSize: '0.9rem',
    fontWeight: 600,
  },
  navLinkActive: { background: INK_SOFT, color: '#fff', boxShadow: 'inset 3px 0 0 var(--ep-accent, #4f46e5)' },
  navIcon: { fontSize: '1rem', width: 20, textAlign: 'center' },
  sidebarFoot: { borderTop: '1px solid #1e293b', padding: 16 },
  who: { marginBottom: 10 },
  whoName: { fontSize: '0.85rem', fontWeight: 700, color: '#fff' },
  whoEmail: { fontSize: '0.75rem', color: '#9aa4b2' },
  logoutBtn: {
    width: '100%',
    background: 'transparent',
    border: '1px solid #334155',
    color: '#e2e8f0',
    borderRadius: 6,
    padding: '7px 12px',
    cursor: 'pointer',
    fontSize: '0.8rem',
    fontWeight: 600,
  },
  content: { flex: 1, minWidth: 0, padding: '28px 32px', maxWidth: 1200, margin: '0 auto', width: '100%', boxSizing: 'border-box' },
};
